import * as z from 'zod';
import { campaignRefSchema, readCampaignRefSchema } from '../campaigns';
import { blockTypeSchema, cssSelectorMapSchema, tidsSchema } from '../common';

// 1. Reference schema with label field (for dropdowns, selects, links)
export const readBlockRefSchema = z.object({
  id: z.uuid(),
  label: z.string().nullable(),
});
export type ReadBlockRef = z.infer<typeof readBlockRefSchema>;

// 2. Minimal reference schema (for foreign keys)
export const blockRefSchema = z.object({
  id: z.uuid(),
});
export type BlockRef = z.infer<typeof blockRefSchema>;

// 3. Read schema - all properties nullable (API response)
export const readBlockSchema = z.object({
  id: z.uuid().nullable(),
  campaign: z.lazy(() => readCampaignRefSchema).nullable(),
  cssSelectors: cssSelectorMapSchema.nullable(),
  value: z.string().nullable(),
  valueAsText: z.string().nullable(),
  status: z.string().nullable(),
  type: blockTypeSchema.nullable(),
  sectionCssSelector: z.string().nullable(),
  label: z.string().nullable(),
  priority: z.number().nullable(),
  track: z.boolean().nullable(),
  eventName: z.string().nullable(),
  enabled: z.boolean().nullable(),
  tids: tidsSchema.nullable(),
  // Logo rendering config — relevant when `type` is `customer_logo`;
  // null/unused for other block types (see embeddedBlockSchema for details).
  greyscale: z.boolean().nullable(),
  dark: z.boolean().nullable(),
  largeLogo: z.boolean().nullable(),
  logoFillColor: z.string().nullable(),
  // Number of <img> logo slots in the originally captured carousel (read-only).
  logoCount: z.number().nullable(),
  // The landing-page version the block was identified against (read-only).
  // Ids rotate with each successful re-analysis — don't cache them across runs.
  landingPageVersionId: z.uuid().nullable(),
  createdAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime().nullable(),
});
export type ReadBlock = z.infer<typeof readBlockSchema>;

// 4. Create schema - based on DTO serialization groups
export const createBlockSchema = z.object({
  campaign: z.lazy(() => campaignRefSchema),
  cssSelectors: cssSelectorMapSchema,
  value: z.string().optional(),
  status: z
    .string()
    .max(100, 'Status must not exceed 100 characters')
    .optional(),
  sectionCssSelector: z
    .string()
    .max(1000, 'Section CSS selector must not exceed 1000 characters')
    .optional(),
  label: z.string().max(500, 'Label must not exceed 500 characters').optional(),
  priority: z.number().min(0).max(100).optional(),
  track: z.boolean().optional(),
  eventName: z.string().nullable(),
  enabled: z.boolean().nullable(),
});
export type CreateBlock = z.infer<typeof createBlockSchema>;

// 5. Update schema - based on DTO serialization groups (usually partial)
export const updateBlockSchema = z.object({
  cssSelectors: cssSelectorMapSchema.optional(),
  value: z.string().optional(),
  status: z
    .string()
    .max(100, 'Status must not exceed 100 characters')
    .optional(),
  sectionCssSelector: z
    .string()
    .max(1000, 'Section CSS selector must not exceed 1000 characters')
    .optional(),
  label: z.string().max(500, 'Label must not exceed 500 characters').optional(),
  priority: z.number().min(0).max(100).optional(),
  track: z.boolean().optional(),
  eventName: z.string().nullable(),
  enabled: z.boolean().nullable(),
  // Render logos in greyscale. Relevant when `type` is `customer_logo`;
  // null/unused for other block types.
  greyscale: z.boolean().nullable().optional(),
  // Render logos using the dark-theme variant. Relevant when `type` is
  // `customer_logo`; null/unused for other block types. Combines with
  // `greyscale` to select which company logo variant the edge renders.
  dark: z.boolean().nullable().optional(),
  // Render logos at the larger size variant. Relevant when `type` is
  // `customer_logo`; null/unused for other block types. Pure passthrough
  // carried to the Cloudflare edge for the SDK to size the logo component.
  largeLogo: z.boolean().nullable().optional(),
  // Recolor logos client-side into a flat silhouette of this hex color
  // (e.g. "#FFFFFF"), for colored/photo backgrounds. Relevant when `type` is
  // `customer_logo`; overrides `greyscale`/`dark`. Null = normal rendering.
  logoFillColor: z
    .string()
    .regex(
      /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/,
      'Logo fill color must be a hex color like #FFFFFF',
    )
    .nullable()
    .optional(),
});
export type UpdateBlock = z.infer<typeof updateBlockSchema>;

// 6. Patch schema - all fields optional (partial update). The personalization
// toggle (`enabled`) goes through here: variants are generated for every block
// regardless of the flag, so toggling never triggers a regeneration — the
// server only flags the campaign's published pages for republication.
export const patchBlockSchema = updateBlockSchema.partial();
export type PatchBlock = z.infer<typeof patchBlockSchema>;

// Legacy export for backward compatibility
export const blockSchema = readBlockSchema;
export type Block = ReadBlock;
